import React from 'react';
import { SEO } from './seo/SEO';
import { client } from '../lib/sanity';
import type { Pet } from '../types/sanity';
import { useParams, useNavigate } from 'react-router-dom';
import { PetCard } from './pets/PetCard';
import { Pagination } from './common/Pagination';
import { PetFilters } from './pets/PetFilters';
import { ActiveFilters } from './pets/ActiveFilters';
import { SearchBar } from './pets/SearchBar';
import { getBreadcrumbSchema } from '../lib/jsonLd';
import { Filter, X } from 'lucide-react';

interface Filters {
  mainCategory?: string[];
  age?: string[];
  gender?: string[];
  size?: string[];
  tags?: string[];
  status?: string[];
}

export function FeaturedPets() {
  const [pets, setPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filters, setFilters] = React.useState<Filters>({});
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showFiltersMobile, setShowFiltersMobile] = React.useState(false);
  const itemsPerPage = 12;
  const { category, subtype } = useParams();
  const navigate = useNavigate();

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + 
      (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} mies.`;
    }
    
    const age = Math.floor(ageInMonths / 12);
    return `${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`;
  };

  const filteredPets = React.useMemo(() => {
    return pets.filter(pet => {
      const matchesSearch = pet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          pet.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const ageInMonths = ((new Date()).getFullYear() - (new Date(pet.birthDate)).getFullYear()) * 12 + 
        ((new Date()).getMonth() - (new Date(pet.birthDate)).getMonth());
      
      const matchesAge = !filters.age?.length || filters.age.some(age => {
        if (age === 'young') return ageInMonths <= 24;
        if (age === 'adult') return ageInMonths > 24 && ageInMonths <= 96;
        if (age === 'senior') return ageInMonths > 96;
        return true;
      });

      const matchesGender = !filters.gender?.length || filters.gender.includes(pet.gender);
      const matchesMainCategory = !filters.mainCategory?.length || filters.mainCategory.includes(pet.mainCategory);
      const matchesSize = !filters.size?.length || filters.size.includes(pet.size);
      const matchesTags = !filters.tags?.length || (pet.tags && pet.tags.some(tag => 
        filters.tags?.includes(tag.slug.current)
      ));
      const matchesStatus = !filters.status?.length || filters.status.includes(pet.status);

      return matchesSearch && matchesAge && matchesGender && matchesMainCategory && matchesSize && matchesTags && matchesStatus;
    });
  }, [pets, searchQuery, filters]);
  
  const paginatedPets = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPets.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPets, currentPage]);

  const totalPages = Math.ceil(filteredPets.length / itemsPerPage);

  const isValidPet = (pet: Pet) => {
    return pet._id && pet.name && pet.image && pet.image.asset && pet.description;
  };

  const handleMeetPet = (identifier: string) => {
    navigate(`/zwierze/${identifier}`);
  };

  React.useEffect(() => {
    const fetchPets = async () => {
      try {
        let query = '*[_type == "pet"';
        
        if (category && subtype) {
          const mainCategory = category === 'cats' ? 'cat' : 'dog';
          const subtypeMap = {
            'dog': 'adult_dog',
            'puppies': 'puppy',
            'female': category === 'cats' ? 'female_cat' : 'female_dog',
            'male': 'male_cat',
            'kittens': 'kitten'
          };
          
          query += ` && mainCategory == "${mainCategory}" && subCategory == "${subtypeMap[subtype as keyof typeof subtypeMap]}"`;
        }
        
        query += ' && !(_id in path("drafts.**"))] | order(_createdAt desc)';

        const result = await client.fetch<Pet[]>(`
          ${query} | order(status asc) {
            _id,
            name,
            slug,
            birthDate,
            subCategory,
            image,
            description,
            gender,
            size,
            status,
            isUrgentFundraising,
            fundraisingGoal,
            fundraisingCurrent,
            fundraisingDescription,
            "tags": tags[]-> {
              _type,
              _ref,
              label,
              "slug": slug
            },
            mainCategory,
            gallery
          }
        `);

        const validPets = result ? result.filter(isValidPet) : [];
        setPets(validPets);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching pets:', err);
        setError('Nie udało się załadować zwierząt');
        setLoading(false);
      }
    };

    fetchPets();
  }, [category, subtype]);

  if (loading) {
    return (
      <section className="py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-xl text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              Ładowanie...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-xl text-accessible-red-600">
              {error}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Generate SEO data based on category and subtype
  const getSEOData = () => {
    if (!category) {
      return {
        title: 'Zwierzaki do adopcji – psy i koty',
        description: 'Odkryj naszych podopiecznych: psy, koty, szczeniaki i kocięta czekające na adopcję. Fundacja Znajdki ratuje bezdomne zwierzęta i pomaga im znaleźć nowy dom.',
        url: '/zwierzeta',
        image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
      };
    }
    
    if (category === 'cats') {
      if (subtype === 'kittens') {
        return {
          title: 'Koty do adopcji – kocięta',
          description: 'Adoptuj kociaka! Młode koty czekające na adopcję w Fundacji Znajdki. Znajdź swojego małego przyjaciela.',
          url: '/zwierzeta',
          image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
        };
      }
      return {
        title: 'Koty do adopcji',
        description: 'Adoptuj kota! Koty i kocięta czekające na adopcję w Fundacji Znajdki. Znajdź swojego futrzastego przyjaciela.',
        url: '/zwierzeta',
        image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
      };
    }
    
    if (category === 'dogs') {
      if (subtype === 'puppies') {
        return {
          title: 'Psy do adopcji – szczeniaki',
          description: 'Adoptuj szczeniaka! Młode psy czekające na adopcję w Fundacji Znajdki. Znajdź swojego puszystego przyjaciela.',
          url: '/zwierzeta',
          image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
        };
      }
      return {
        title: 'Psy do adopcji',
        description: 'Adoptuj psa! Psy i szczeniaki czekające na adopcję w Fundacji Znajdki. Znajdź swojego czworonożnego przyjaciela.',
        url: '/zwierzeta',
        image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
      };
    }
    
    return {
      title: 'Zwierzaki do adopcji',
      description: 'Psy, koty, szczeniaki i kocięta czekające na adopcję w Fundacji Znajdki.',
      url: '/zwierzeta',
      image: '/images/animals/fundacja-znajdki-zwierzeta-001.webp'
    };
  };

  const seoData = getSEOData();
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Zwierzaki', url: 'https://znajdki.pl/zwierzeta' }
  ]);

  return (
    <section className="py-16 bg-primary/5 dark:bg-primary/20 high-contrast:bg-black">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        url={seoData.url}
        image={seoData.image}
        jsonLd={breadcrumbSchema}
      />
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="text-center mb-8">
          <h2 className="section-title mb-4">
            {(() => {
              if (!category) return 'Nasi podopieczni';
              if (!subtype) return category === 'cats' ? 'Nasi podopieczni – koty' : 'Nasi podopieczni – psy';
              
              const subtitleMap = {
                'puppies': 'Szczeniaki',
                'dog': 'Psy',
                'female': category === 'cats' ? 'Kotki' : 'Sunie',
                'male': 'Kocury',
                'kittens': 'Kociaki'
              };
              
              return subtitleMap[subtype as keyof typeof subtitleMap];
            })()} pod opieką
          </h2>
          <p className="body-large">
            {(() => {
              if (!category) return 'Poznaj naszych podopiecznych – część z nich czeka na dom, inni wymagają leczenia i nie są dostępni do adopcji.';
              if (!subtype) return `Poznaj naszych podopiecznych – ${category === 'cats' ? 'koty' : 'psy'}. Nie wszystkie zwierzęta są dostępne do adopcji.`;
              
              const descriptionMap = {
                'puppies': 'Poznaj nasze szczeniaki pod opieką fundacji – część z nich może być jeszcze niedostępna do adopcji.',
                'dog': 'Poznaj nasze psy pod opieką – nie wszystkie są aktualnie do adopcji.',
                'female': category === 'cats' ? 'Poznaj nasze kotki pod opieką – część może nie być dostępna do adopcji.' : 'Poznaj nasze sunie pod opieką – część może nie być dostępna do adopcji.',
                'male': 'Poznaj nasze kocury pod opieką – część może nie być dostępna do adopcji.',
                'kittens': 'Poznaj nasze kociaki pod opieką – część może nie być dostępna do adopcji.'
              };
              
              return descriptionMap[subtype as keyof typeof descriptionMap];
            })()}
          </p>
        </div>

        {/* Mobile Filter Toggle Button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
            className="w-full flex items-center justify-between bg-accessible-red-700 dark:bg-accessible-red-800 text-white px-6 py-4 rounded-lg shadow-lg hover:bg-accessible-red-800 transition-colors"
            aria-label={showFiltersMobile ? 'Ukryj filtry' : 'Pokaż filtry'}
          >
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5" />
              <span className="btn-text-medium">Filtry</span>
              {Object.values(filters).some(value => value?.length > 0) && (
                <span className="bg-white text-accessible-red-700 px-2 py-0.5 rounded-full text-xs">
                  Aktywne
                </span>
              )}
            </div>
            {showFiltersMobile ? <X className="w-5 h-5" /> : <Filter className="w-5 h-5" />}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters - Hidden on mobile by default, visible when showFiltersMobile is true or on desktop */}
          <aside className={`lg:w-[300px] flex-shrink-0 self-start ${showFiltersMobile ? 'block' : 'hidden'} lg:block`} aria-label="Filtry wyszukiwania">
            <div className="bg-accessible-red-700 dark:bg-accessible-red-800 high-contrast:bg-black rounded-lg shadow-lg p-6 lg:sticky lg:top-24 lg:mt-[3px] text-white">
              <div className="mb-6">
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              </div>
              <PetFilters filters={filters} setFilters={setFilters} pets={pets} />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1" aria-label="Lista zwierząt">
            <ActiveFilters filters={filters} setFilters={setFilters} pets={pets} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {filteredPets.length > 0 ? paginatedPets.map((pet) => (
                <PetCard
                  key={pet._id}
                  pet={pet}
                  onMeetPet={handleMeetPet}
                  calculateAge={calculateAge}
                />
              )) : (
                <div className="col-span-3 text-center py-8">
                  <p className="body-large">
                    {searchQuery || Object.keys(filters).length
                      ? 'Nie znaleziono zwierząt spełniających kryteria wyszukiwania'
                      : 'Brak zwierząt do wyświetlenia w tej kategorii'}
                  </p>
                </div>
              )}
            </div>
            
            {filteredPets.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="mt-8"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}